import React from 'react';
import {
  CalendarCheck,
  Video,
  MessageCircle,
  BookOpen,
  FileText,
  ShieldCheck,
  Sparkles,
  ExternalLink,
} from 'lucide-react';
import { HELLO_SUPPORT_FEATURES } from '../data/mock';
import { Button } from './ui/button';

const iconMap = {
  CalendarCheck,
  Video,
  MessageCircle,
  BookOpen,
  FileText,
  ShieldCheck,
};

const HelloSupport = () => {

  return (
    <section id="hello-support" className="py-24 bg-gradient-to-br from-[#0B3B44] via-[#134A56] to-[#0B3B44] relative overflow-hidden">
      {/* Decorative */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-[#F55B26]/20 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#FFB347]/10 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-14 items-center mb-16">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#F55B26]/20 border border-[#F55B26]/40 text-[#FFB347] rounded-full text-sm font-semibold">
              <Sparkles className="w-4 h-4" />
              New Initiative · Launching 2027
            </div>

            <h2 className="text-4xl lg:text-6xl font-extrabold text-white leading-[1.05]">
              Introducing <span className="text-[#FFB347]">Hello Support</span>
            </h2>
            <p className="text-xl text-white/90 leading-relaxed">
              A Hello Foundation Initiative &mdash; free, confidential online counselling
              for South African children, teenagers and their caregivers.
            </p>
            <p className="text-base text-white/75 leading-relaxed">
              Mental health support shouldn&apos;t have a waiting list or a price tag.
              Young South Africans face high levels of anxiety, depression, bullying, and
              exposure to violence. Hello Support closes that gap by offering early,
              accessible, professional counselling &mdash; before challenges become crises.
            </p>

            <div className="grid grid-cols-3 gap-4 pt-2">
              {[
                { v: 'Free', l: 'Always' },
                { v: '50min', l: 'Per session' },
                { v: '100%', l: 'Confidential' },
              ].map((s, i) => (
                <div key={i} className="bg-white/10 backdrop-blur rounded-2xl border border-white/20 p-4 text-center">
                  <div className="text-2xl font-extrabold text-[#FFB347]">{s.v}</div>
                  <div className="text-xs text-white/70 uppercase tracking-widest mt-1">{s.l}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3 pt-3">
              <a
                href="https://www.hellosupport.co.za"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="bg-[#F55B26] hover:bg-[#E14D1F] text-white font-semibold px-7 py-6 rounded-full shadow-lg">
                  Visit hellosupport.co.za
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <a
                href="https://www.hellosupport.co.za/register"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  variant="outline"
                  className="bg-transparent border-2 border-white/40 text-white hover:bg-white hover:text-[#0B3B44] font-semibold px-7 py-6 rounded-full"
                >
                  Preview Registration
                </Button>
              </a>
            </div>
          </div>

          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white/10">
              <img
                src="https://images.unsplash.com/photo-1594913543505-e4fdd1d021e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHx5b3V0aCUyMHdlbGxuZXNzfGVufDB8fHx8MTc4MzA1OTcwNXww&ixlib=rb-4.1.0&q=85"
                alt="Youth wellness"
                className="w-full h-[520px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B3B44]/60 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur rounded-2xl p-4 shadow-xl">
                <p className="text-sm text-slate-700 italic">
                  &ldquo;I didn&apos;t think I could talk to anyone.&rdquo;
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Through Hello Support, Lerato (16) reached out for the first time.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="mb-16">
          <h3 className="text-2xl lg:text-3xl font-extrabold text-white mb-8 text-center">
            Everything you need in one calm place.
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {HELLO_SUPPORT_FEATURES.map((f, i) => {
              const Icon = iconMap[f.icon];
              return (
                <div
                  key={i}
                  className="group bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl p-6 hover:bg-white/12 hover:border-[#FFB347]/40 transition-all hover:-translate-y-1"
                  style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
                >
                  <div className="w-12 h-12 rounded-xl bg-[#F55B26]/20 border border-[#F55B26]/30 flex items-center justify-center mb-4 group-hover:bg-[#F55B26] transition-colors">
                    {Icon && <Icon className="w-6 h-6 text-[#FFB347] group-hover:text-white transition-colors" />}
                  </div>
                  <h4 className="font-bold text-white text-lg mb-2">{f.title}</h4>
                  <p className="text-sm text-white/70 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HelloSupport;
